/**
 * Срез анализа — ОДИН орган управления двумя осями среза.
 *
 * Дата состояния (`as_of`) и «показывать ли демо» (`include_demo`) — не
 * настройка экрана, а параметры вопроса: меняя их, человек меняет ВСЕ
 * канонические виды сразу. Поэтому орган один и монтируется оболочкой, а не
 * экранами; оболочка Astra ставит его ровно на маршрутах с `source === 'api'`
 * (на экране фикстур эти ручки ничего не меняли бы и врали бы о
 * происхождении чисел).
 *
 * Вид (agroai-6al.38, журнал №350). Раньше это была отдельная полоса над
 * каждой страницей: подпись «ДАТА СОСТОЯНИЯ» в две строки, пустое поле,
 * галка демо — тяжёлая строка, которая в 95 % случаев говорила «ничего не
 * выбрано». Теперь:
 *
 * * `AnalysisContextChip` — компактный чип в шапке: «Как знаем сейчас ▾» или
 *   «На 12 сен 2026 ▾» (+ «без демо», если демо выключено). Нажатие открывает
 *   поповер с полем даты, сбросом и переключателем демо — с теми же
 *   пояснениями, что были у полосы;
 * * `AnalysisAsOfNotice` — плашка над содержимым ТОЛЬКО когда дата задана:
 *   срез в прошлом — особое состояние, оно не должно жить молча в чипе.
 *
 * Поведение то же: значения читаются и пишутся через `useAnalysisContext`
 * (параметры адреса `as_of`, `include_demo`), второго состояния нет.
 */

import { CalendarDays, ChevronDown, History } from "lucide-react";
import { Button, Dialog, DialogTrigger, Popover } from "react-aria-components";
import { useEffect, useState } from "react";
import { useAnalysisContext } from "./context";
import { localIsoDate } from "../lib/localDate";
import { useServerToday } from "./useSliceDate";
import { DateField } from "../components/DateField";
import { InfoPopover } from "../components/InfoPopover";
import { defineMessages } from "../i18n/define";
import { useT } from "../i18n/hooks";
import { tr } from "../i18n/runtime";
import { dateShort } from "../astra/lib/format";
import styles from "./ContextBar.module.css";

const m = defineMessages({
  needFullDate: { id: "shell.context.needFullDate", defaultMessage: "Введите дату полностью: день, месяц и год из четырёх цифр.", description: "Ошибка под полем «Дата состояния» в поповере среза данных: дата набрана не до конца" },
  outOfRange: { id: "shell.context.outOfRange", defaultMessage: "Срез — с {min} по сегодня ({today}).", description: "Ошибка под полем «Дата состояния»: дата вне допустимого диапазона; {min} и {today} — готовые даты («1 янв 2020»)" },
  asOfNow: { id: "shell.context.asOfNow", defaultMessage: "Как знаем сейчас", description: "Текст чипа среза в шапке, когда дата не выбрана: показано последнее известное состояние; коротко" },
  asOfDate: { id: "shell.context.asOfDate", defaultMessage: "На {date}", description: "Текст чипа среза в шапке, когда выбрана дата; {date} — готовая дата («12 сен 2026»); коротко" },
  groupLabel: { id: "shell.context.groupLabel", defaultMessage: "Контекст анализа", description: "aria-label группы чипа среза данных в шапке (для экранных дикторов)" },
  chipLabel: {
    id: "shell.context.chipLabel",
    defaultMessage: "Срез данных: {state}{demo, select, yes {} other {, без демо-данных}}. Изменить",
    description: "aria-label кнопки-чипа среза в шапке; {state} — «как знаем сейчас» или «на 12 сен 2026» строчными, {demo} — yes, если демо-данные включены",
  },
  noDemoTag: { id: "shell.context.noDemoTag", defaultMessage: "без демо", description: "Метка на чипе среза: демо-данные выключены; очень коротко" },
  dialogLabel: { id: "shell.context.dialogLabel", defaultMessage: "Срез данных", description: "aria-label окна выбора среза данных (поповер из шапки)" },
  popTitle: { id: "shell.context.popTitle", defaultMessage: "Срез данных", description: "Заголовок окна выбора среза данных" },
  popNote: { id: "shell.context.popNote", defaultMessage: "Меняет числа на всех экранах сразу — иначе экраны разошлись бы.", description: "Пояснение под заголовком окна среза данных" },
  dateLabel: { id: "shell.context.dateLabel", defaultMessage: "Дата состояния", description: "Подпись поля даты в окне среза данных" },
  clearDate: { id: "shell.context.clearDate", defaultMessage: "Очистить дату состояния", description: "aria-label кнопки-крестика в поле даты среза" },
  dateExplain: {
    id: "shell.context.dateExplain",
    defaultMessage:
      "Все числа на всех экранах считаются на этот день. Пустое значение — не «сегодня», а «как знаем сейчас»: последнее состояние корпуса без отсечки по дате. Дата применяется по Enter, «Применить» или уходу из поля.",
    description: "Всплывающее пояснение (знак вопроса) у поля «Дата состояния»; «корпус» — набор принятых данных",
  },
  dateEmptyHint: { id: "shell.context.dateEmptyHint", defaultMessage: "Пусто — как знаем сейчас", description: "Подсказка под пустым полем даты среза" },
  apply: { id: "shell.context.apply", defaultMessage: "Применить", description: "Кнопка в окне среза: применить набранную дату; коротко" },
  reset: { id: "shell.context.reset", defaultMessage: "Смотреть как знаем сейчас", description: "Кнопка в окне среза: сбросить дату и вернуться к последнему состоянию" },
  showDemo: { id: "shell.context.showDemo", defaultMessage: "Показывать демо-данные", description: "Подпись переключателя в окне среза данных" },
  demoLabel: { id: "shell.context.demoLabel", defaultMessage: "Демо-данные", description: "Предмет пояснения у переключателя демо-данных (для экранных дикторов и заголовка поповера)" },
  demoExplain: { id: "shell.context.demoExplain", defaultMessage: "По умолчанию включено: операции стенда синтетические. Выключите, чтобы видеть только фактические записи.", description: "Всплывающее пояснение у переключателя демо-данных" },
  noticeText: {
    id: "shell.context.noticeText",
    defaultMessage: "Показан срез на <b>{date}</b> — все числа посчитаны на эту дату.",
    description: "Плашка над содержимым, когда выбрана дата среза; {date} — готовая дата; <b> выделяет дату",
  },
  noticeBack: { id: "shell.context.noticeBack", defaultMessage: "Вернуться к «как знаем сейчас»", description: "Кнопка-ссылка на плашке среза: сбросить дату" },
});

/** «4 окт 2026» из ISO календарного дня, без часовых поясов. */
export function sliceDateLabel(iso: string): string {
  if (!/^(\d{4})-(\d{2})-(\d{2})$/.test(iso)) return iso;
  const label = dateShort(iso);
  return label === "—" ? iso : label;
}

/** Ранняя граница среза: раньше этой даты в корпусе нет сезонов. */
export const SLICE_MIN = "2020-01-01";

/**
 * Почему дата НЕ может стать срезом, или `null`, если может (agroai-6al.40).
 *
 * Поле даты отдаёт значение на каждом нажатии: набирая год «2026», человек
 * проходит через 0002, 0020, 0202. Если каждое из них уходит в адрес, каждое
 * перечитывает права и все экраны (всплеск `/auth/me` и 422 на 0002-09-01).
 * Срезом становится только полная дата в диапазоне [SLICE_MIN, сегодня].
 */
export function sliceDateProblem(iso: string, today: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return tr(m.needFullDate);
  if (iso < SLICE_MIN || iso > today) {
    return tr(m.outOfRange, { min: sliceDateLabel(SLICE_MIN), today: sliceDateLabel(today) });
  }
  return null;
}

export function AnalysisContextChip() {
  const { asOf, includeDemo, setAsOf, setIncludeDemo } = useAnalysisContext();
  const t = useT();
  const dateText = asOf ? t(m.asOfDate, { date: sliceDateLabel(asOf) }) : t(m.asOfNow);
  // Позже «сегодня» сервера срез не бывает; часы браузера — страховка до ответа.
  const today = useServerToday() ?? localIsoDate();

  // Черновик поля живёт здесь; в адрес он уходит только целиком и только по
  // явному шагу: Enter, уход фокуса из поля, «Применить», закрытие поповера.
  const [draft, setDraft] = useState<string | null>(asOf);
  useEffect(() => setDraft(asOf), [asOf]);
  const problem = draft ? sliceDateProblem(draft, today) : null;
  const dirty = draft !== asOf;

  const commit = () => {
    if (!dirty || problem) return;
    setAsOf(draft);
  };

  return (
    <div className={styles.chipWrap} role="group" aria-label={t(m.groupLabel)}>
      <DialogTrigger
        onOpenChange={(open) => {
          if (open) return;
          // Закрытие — тоже «готово»: верная дата применяется, неверная
          // отбрасывается, а не висит в поле до следующего открытия.
          if (problem) setDraft(asOf);
          else commit();
        }}
      >
        <Button
          className={`${styles.chip} ${asOf ? styles.chipSet : ""}`}
          aria-label={t(m.chipLabel, { state: dateText.toLowerCase(), demo: includeDemo ? "yes" : "no" })}
        >
          <CalendarDays size={16} strokeWidth={1.75} aria-hidden />
          <span className={styles.chipText}>{dateText}</span>
          {includeDemo ? null : <span className={styles.chipTag}>{t(m.noDemoTag)}</span>}
          <ChevronDown size={14} strokeWidth={1.75} aria-hidden />
        </Button>
        <Popover className={styles.popover} placement="bottom end" offset={6}>
          <Dialog className={styles.dialog} aria-label={t(m.dialogLabel)}>
            <div className={styles.popTitle}>{t(m.popTitle)}</div>
            <p className={styles.popNote}>
              {t(m.popNote)}
            </p>

            <div
              className={styles.dateRow}
              // Перехват на погружении: сегменты даты сами гасят всплытие клавиш.
              onKeyDownCapture={(event) => {
                if (event.key === "Enter") commit();
              }}
              onBlur={(event) => {
                const next = event.relatedTarget as Node | null;
                if (!next || !event.currentTarget.contains(next)) commit();
              }}
            >
              <DateField
                size="sm"
                label={t(m.dateLabel)}
                value={draft}
                onChange={setDraft}
                isClearable
                clearLabel={t(m.clearDate)}
                isInvalid={Boolean(problem)}
                errorMessage={problem}
                explain={t(m.dateExplain)}
                description={problem ? undefined : draft ? undefined : t(m.dateEmptyHint)}
              />
              {dirty ? (
                <Button className={styles.apply} isDisabled={Boolean(problem)} onPress={commit}>
                  {t(m.apply)}
                </Button>
              ) : null}
            </div>

            {asOf ? (
              <Button
                className={styles.reset}
                onPress={() => {
                  setDraft(null);
                  setAsOf(null);
                }}
              >
                <History size={16} strokeWidth={1.75} aria-hidden />
                {t(m.reset)}
              </Button>
            ) : null}

            <div className={styles.demoRow}>
              <input
                id="include-demo"
                type="checkbox"
                checked={includeDemo}
                onChange={(event) => setIncludeDemo(event.target.checked)}
              />
              <label htmlFor="include-demo">{t(m.showDemo)}</label>
              <InfoPopover icon="info" label={t(m.demoLabel)}>
                {t(m.demoExplain)}
              </InfoPopover>
            </div>
          </Dialog>
        </Popover>
      </DialogTrigger>
    </div>
  );
}

/**
 * Плашка «показан срез на дату». Только когда дата задана: «как знаем сейчас»
 * — обычное состояние и места на странице не занимает.
 */
export function AnalysisAsOfNotice() {
  const { asOf, setAsOf } = useAnalysisContext();
  const t = useT();
  if (!asOf) return null;
  return (
    <div className={styles.notice} role="status">
      <History size={16} strokeWidth={1.75} aria-hidden />
      <span className={styles.noticeText}>
        {t(m.noticeText, { date: sliceDateLabel(asOf), b: (chunks) => <strong>{chunks}</strong> })}
      </span>
      <button type="button" className={styles.noticeAction} onClick={() => setAsOf(null)}>
        {t(m.noticeBack)}
      </button>
    </div>
  );
}

/**
 * Прежняя каноническая оболочка (`components/AppShell`) — чип и плашка одной
 * строкой. Оболочка Astra ставит их раздельно: чип в шапку, плашку над
 * содержимым.
 */
export function AnalysisContextBar() {
  return (
    <div className={styles.bar}>
      <AnalysisContextChip />
      <AnalysisAsOfNotice />
    </div>
  );
}
